/**
 * French catalogue. French is the default locale.
 *
 * The `errors` block must carry an entry for every code in `ERROR_CODES` from
 * `@hb/contracts` - a test enforces it. The server sends a machine code and
 * never a sentence, so an untranslated code would surface to the user as raw
 * text like `auth.totp_invalid`.
 */
export const fr = {
    common: {
        appName: 'Honoré Bagni',
        save: 'Enregistrer',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        delete: 'Supprimer',
        close: 'Fermer',
        back: 'Retour',
        next: 'Suivant',
        search: 'Rechercher',
        loading: 'Chargement…',
        retry: 'Réessayer',
        copy: 'Copier',
        copied: 'Copié',
        yes: 'Oui',
        no: 'Non',
        continue: 'Continuer',
        optional: 'facultatif',
        none: 'Aucun',
    },
    nav: {
        account: 'Mon compte',
        admin: 'Administration',
        skipToContent: 'Aller au contenu',
        chat: 'Discussion',
        projects: 'Projets',
        settings: 'Réglages',
        photos: 'Photos',
        backup: 'Sauvegarde',
    },
    auth: {
        signIn: 'Se connecter',
        signOut: 'Se déconnecter',
        signUp: 'Créer un compte',
        email: 'Adresse e-mail',
        password: 'Mot de passe',
        newPassword: 'Nouveau mot de passe',
        currentPassword: 'Mot de passe actuel',
        forgotPassword: 'Mot de passe oublié ?',
        resetPassword: 'Réinitialiser le mot de passe',
        // Deliberately non-committal: the API never reveals whether an address is
        // registered, so the wording must be true either way.
        signupAccepted: 'Si cette adresse peut être utilisée, vous recevrez un e-mail dans quelques instants.',
        resetAccepted: 'Si un compte existe pour cette adresse, un e-mail de réinitialisation vient de partir.',
        verifyEmailTitle: 'Vérifiez votre adresse e-mail',
        verifyEmailSent: 'Nous avons envoyé un lien de vérification à {email}.',
        resendVerification: 'Renvoyer l’e-mail',
        passwordRequirements: 'Au moins 12 caractères. Une phrase de passe est plus sûre qu’un mot compliqué.',
        signUpTitle: 'Créer votre compte',
        signUpSubtitle: 'Un compte unique pour les photos et la discussion.',
        signInTitle: 'Content de vous revoir',
        signInSubtitle: 'Saisissez votre mot de passe, puis votre code à deux facteurs.',
        noAccount: 'Pas encore de compte ?',
        haveAccount: 'Vous avez déjà un compte ?',
        verifying: 'Vérification de votre adresse…',
        verifyFailed: 'Ce lien n’est plus valable. Demandez-en un nouveau depuis la page de connexion.',
        emailResent: 'C’est reparti. Vérifiez votre boîte de réception.',
        forgotTitle: 'Mot de passe oublié',
        forgotSubtitle: 'Nous vous enverrons un lien pour en choisir un nouveau.',
        resetTitle: 'Choisir un nouveau mot de passe',
        // Consuming a reset link revokes every session, so this is not a detail.
        resetSubtitle: 'Tous vos appareils seront déconnectés.',
        resetDone: 'Mot de passe modifié. Vous pouvez vous reconnecter.',
        changePassword: 'Changer de mot de passe',
        // A change keeps the current device and revokes the others.
        changePasswordHint: 'Vos autres appareils seront déconnectés.',
        passwordChanged: 'Mot de passe modifié.',
    },
    totp: {
        title: 'Authentification à deux facteurs',
        required: 'La double authentification est obligatoire pour tous les comptes.',
        scanQr: 'Scannez ce QR code avec votre application d’authentification.',
        manualEntry: 'Ou saisissez cette clé manuellement :',
        enterCode: 'Saisissez le code à 6 chiffres',
        code: 'Code de vérification',
        useRecoveryCode: 'Utiliser un code de récupération',
        recoveryCode: 'Code de récupération',
        recoveryTitle: 'Vos codes de récupération',
        // The one moment these are ever shown; they are stored hashed.
        recoveryWarning: 'Conservez ces codes en lieu sûr. Ils ne seront plus jamais affichés, et chacun ne sert qu’une fois.',
        recoveryDownload: 'Télécharger les codes',
        enrolTitle: 'Activer la double authentification',
        enrolSubtitle: 'Dernière étape avant d’utiliser votre compte.',
        qrAlt: 'QR code contenant votre clé secrète',
        confirmCode: 'Activer',
        challengeSubtitle: 'Ouvrez votre application d’authentification.',
        useAuthenticator: 'Utiliser l’application d’authentification',
        deviceLabel: 'Nom de cet appareil',
        deviceLabelHint: 'Pour reconnaître cet appareil dans la liste. Par exemple « Portable de Nathan ».',
        recoverySaved: 'J’ai enregistré mes codes',
    },
    account: {
        title: 'Mon compte',
        language: 'Langue',
        plan: 'Formule',
        usage: 'Utilisation',
        sessions: 'Appareils connectés',
        thisDevice: 'Cet appareil',
        revokeSession: 'Déconnecter cet appareil',
        signOutEverywhere: 'Se déconnecter partout',
        lastSeen: 'Vu {when}',
        deleteAccount: 'Supprimer mon compte',
        exportData: 'Exporter mes données',
        signedInSince: 'Connecté depuis {when}',
        theme: 'Thème',
        themeSystem: 'Système',
        themeDark: 'Sombre',
        themeLight: 'Clair',
        applications: 'Applications',
        noOtherDevices: 'Aucun autre appareil connecté.',
        unnamedDevice: 'Appareil sans nom',
        planExpires: 'Expire le {when}',
        planNeverExpires: 'Sans expiration',
        usageOf: '{used} sur {limit}',
        localeSaved: 'Langue enregistrée.',
    },
    // Deleting an account: the card on the account screen, the form, the screen
    // that says when, the button behind the email's link, and the public page a
    // store listing points at. The privacy address is always a parameter: `@`
    // starts a linked message in vue-i18n, so a literal one breaks the screen.
    deletion: {
        cardHint: 'Efface définitivement vos photos, vos albums, vos groupes et vos conversations. Vous aurez une heure pour changer d’avis.',
        title: 'Supprimer votre compte',
        whatGoes: 'Tout ce que contient votre compte sera effacé : vos photos et vidéos, vos albums, les groupes que vous avez créés, vos conversations et leurs fichiers, et votre clé d’API. Les membres de vos groupes n’y auront plus accès.',
        grace: 'Rien n’est effacé tout de suite. Tous vos appareils seront déconnectés, et nous vous enverrons un lien pour garder votre compte pendant {minutes} minutes. Passé ce délai, la suppression est définitive.',
        backups: 'Des copies peuvent subsister dans nos sauvegardes pendant {days} jours au plus, le temps qu’elles expirent.',
        confirm: 'Pour confirmer, saisissez votre mot de passe et un code de votre application d’authentification.',
        submit: 'Supprimer mon compte',
        keepInstead: 'Non, garder mon compte',
        scheduledTitle: 'Votre compte va être supprimé',
        scheduledWhen: 'Il sera effacé à {time}. Tous vos appareils ont été déconnectés.',
        scheduledKeep: 'Pour le garder, suivez le lien de l’e-mail que nous venons de vous envoyer avant cette heure-là.',
        scheduledUnknown: 'Votre compte sera effacé dans l’heure. Pour le garder, suivez le lien de l’e-mail que nous venons de vous envoyer.',
        keepTitle: 'Garder votre compte',
        keepIntro: 'Votre compte doit être supprimé dans moins d’une heure. Ce bouton annule la suppression.',
        keepAction: 'Garder mon compte',
        kept: 'Votre compte est conservé. Reconnectez-vous pour l’utiliser.',
        keptAdvice: 'Si vous n’êtes pas à l’origine de cette demande, changez votre mot de passe dès que vous serez connecté.',
        keepFailed: 'Ce lien ne fonctionne plus : il a déjà servi, ou l’heure est passée.',
        wrongPassword: 'Ce mot de passe est incorrect.',
        infoTitle: 'Supprimer votre compte Honoré Bagni',
        infoIntro: 'Vous pouvez supprimer votre compte vous-même, depuis la page de votre compte :',
        infoStepSignIn: 'Connectez-vous.',
        infoStepOpen: 'Ouvrez « Mon compte », puis « Supprimer mon compte ».',
        infoStepConfirm: 'Confirmez avec votre mot de passe et un code à deux facteurs.',
        infoCannotSignIn: 'Vous ne pouvez plus vous connecter, faute de mot de passe et de double authentification ? Écrivez à {address} depuis l’adresse de votre compte, et nous le supprimerons pour vous.',
        infoContinue: 'Continuer vers la suppression',
        infoSignIn: 'Se connecter pour supprimer mon compte',
    },
    admin: {
        title: 'Administration',
        searchLabel: 'Rechercher une adresse e-mail',
        // `@` starts vue-i18n's linked-message syntax, so a bare one at the end of
        // a message fails to compile and takes the whole screen down with it. The
        // `{'...'}` form is how a literal is written.
        searchPlaceholder: "debut-de-ladresse{'@'}",
        filterStatus: 'Statut',
        allStatuses: 'Tous',
        plan: 'Formule',
        grants: 'Accès',
        lock: 'Bloquer',
        unlock: 'Débloquer',
        lastLogin: 'Dernière connexion',
        never: 'Jamais',
        loadMore: 'Charger plus',
        noResults: 'Aucun compte ne correspond.',
        saved: 'Compte mis à jour.',
        // Admin writes require a recently presented second factor; there is no
        // re-elevation endpoint, so the only way back is to sign in again.
        staleSession: 'Reconnectez-vous pour modifier un compte.',
    },
    chat: {
        title: 'Discussion',
        conversations: 'Discussions',
        newConversation: 'Nouvelle discussion',
        noConversations: 'Aucune discussion pour l’instant.',
        conversationsLabel: 'Vos discussions',
        showConversations: 'Afficher les discussions',
        hideConversations: 'Masquer les discussions',
        renameConversation: 'Renommer la discussion',
        deleteConversation: 'Supprimer la discussion',
        conversationTitle: 'Titre de la discussion',
        allProjects: 'Toutes',
        filterByProject: 'Filtrer par projet',
        loadMore: 'Charger plus',
        transcriptLabel: 'Transcription de la discussion',
        emptyTitle: 'Posez votre première question',
        emptySubtitle: 'La réponse s’écrit au fur et à mesure, et reprend toute seule si la connexion est coupée.',
        you: 'Vous',
        assistant: 'Assistant',
        composerLabel: 'Votre message',
        composerPlaceholder: 'Écrivez votre message…',
        // Documents the keyboard shortcut, which is otherwise invisible.
        composerHint: 'Entrée pour envoyer, Maj+Entrée pour aller à la ligne.',
        send: 'Envoyer',
        stop: 'Arrêter',
        // The reply is a row on the server, so leaving and coming back continues it.
        resume: 'Reprendre la réponse',
        retry: 'Réessayer',
        exportLabel: 'Télécharger en',
        exportAction: 'Télécharger',
        exportFormatMd: 'Markdown (.md)',
        exportFormatCsv: 'CSV (.csv)',
        exportFormatDocx: 'Word (.docx)',
        exportFormatXlsx: 'Excel (.xlsx)',
        exportFormatPdf: 'PDF (.pdf)',
        // Demander une image plutôt qu'une réponse. Le mode est sur le composeur
        // parce qu'une image générée est un tour de la conversation, et non une
        // chose à côté.
        modeLabel: 'Ce qu’on envoie',
        modeMessage: 'Message',
        modeImage: 'Image',
        draw: 'Dessiner',
        imageModelLabel: 'Modèle d’image',
        promptPlaceholder: 'Décrivez l’image…',
        // La seule chose que le lecteur ne peut pas deviner : rien n’apparaît avant
        // la fin, et cela prend du temps.
        imageHint: 'Une image demande jusqu’à une minute, et arrive d’un seul coup.',
        drawing: 'Dessin en cours…',
        // Volontairement générique : la seule description disponible est l’invite,
        // qui est le message précédent - et la répéter au lecteur vaut moins que de
        // dire simplement ce que c’est.
        imageAlt: 'Image générée',
        // Lu par la voix du navigateur. NVIDIA n'a pas d'endpoint REST de synthèse
        // vocale, donc rien n'est généré ni téléchargé - voir `speech.ts`.
        readAloud: 'Lire à voix haute',
        stopReading: 'Arrêter la lecture',
        reasoning: 'Raisonnement',
        showReasoning: 'Afficher le raisonnement',
        hideReasoning: 'Masquer le raisonnement',
        thinking: 'Réflexion en cours…',
        generating: 'Rédaction de la réponse…',
        reconnecting: 'Connexion perdue, reprise de la réponse…',
        cancelled: 'Réponse interrompue.',
        model: 'Modèle',
        reasoningEffort: 'Effort de raisonnement',
        // Reasoning is expressed differently by each model family, and some do not
        // reason at all - the control is disabled rather than hidden so the absence
        // is legible.
        // Dit ce qui est vrai - nous n'avons pas de dialecte pour transmettre le
        // réglage - plutôt que « ce modèle ne raisonne pas », que le catalogue ne
        // peut pas garantir. Un modèle sans règle retombe sur une valeur prudente et
        // peut très bien raisonner quand même.
        reasoningUnsupported: 'L’effort de raisonnement n’est pas réglable sur ce modèle.',
        // Chaque modèle proposé porte sa propre fenêtre : la question posée au
        // sélecteur est « lequel a de la place », et n'y répondre que pour le modèle
        // déjà choisi oblige à choisir à l'aveugle puis à en lire la conséquence.
        // Forme compacte, parce que c'est une `<option>` ; le chiffre exact reste
        // dans `contextTokens`.
        modelOption: '{name} · {context} de contexte',
        // A conversation keeps the model that wrote it, so a withdrawn model has to
        // stay nameable long after it stops being choisissable.
        modelRetiredOption: '{name} (plus disponible)',
        modelRetired: 'Le fournisseur ne propose plus ce modèle. Choisissez-en un autre pour continuer cette discussion.',
        accepts: 'Accepte : {modalities}',
        // Toujours donné en entier sous le contrôle, et non redondant avec l'option :
        // à 12rem le select fermé tronque son propre texte, donc sur téléphone c'est
        // le seul endroit où la fenêtre du modèle choisi survit.
        contextTokens: '{tokens} jetons de contexte',
        // Ce que le dernier tour a coûté, face à ce que le modèle peut contenir. La
        // seconde forme n'est pas un repli mais la réponse honnête pour un modèle
        // sans règle : un plafond inventé se lit comme de la place qu'on n'a pas.
        //
        // Le pourcentage vient en tête parce qu'il ne demande aucun calcul : deux
        // nombres à six chiffres sont une soustraction que le lecteur ne devrait pas
        // avoir à faire pour découvrir qu'il est presque plein.
        contextUsedOf: 'Dernier tour : {percent} de la fenêtre — {used} jetons sur {total}',
        contextUsedOfUnknown: 'Dernier tour : {used} jetons (fenêtre inconnue)',
        // Dit explicitement, parce que l'alternative est silencieuse : au-delà de la
        // fenêtre le fournisseur supprime les tours les plus *anciens*, et le modèle
        // semble donc oublier plutôt qu'échouer.
        truncated: 'Le modèle a manqué de place et s’est arrêté en chemin. Reformulez plus brièvement, ou ouvrez une nouvelle discussion.',
        noAnswer: 'Le modèle a passé toute sa réponse à réfléchir sans jamais répondre. Essayez une question plus courte, ou un autre modèle.',
        usage: '{prompt} jetons envoyés, {completion} reçus',
        // Attachments are stored and can be sent, but nothing uploads them yet.
        attach: 'Joindre',
        attachRemove: 'Retirer {name}',
        noKeyTitle: 'Ajoutez votre clé API',
        noKeySubtitle: 'La discussion utilise votre propre clé NVIDIA. Rien n’est envoyé avant que vous n’en ayez enregistré une.',
        noKeyAction: 'Ouvrir les réglages',
    },
    modality: {
        text: 'texte',
        image: 'image',
        audio: 'audio',
    },
    effort: {
        off: 'Désactivé',
        low: 'Faible',
        medium: 'Moyen',
        high: 'Élevé',
    },
    access: {
        signInTitle: 'Connexion requise',
        // The sign-in screen belongs to the identity application, on another
        // origin, so this page is a hop rather than a form.
        signInBody: 'Nous vous emmenons à la page de connexion.',
        signInAction: 'Continuer vers la connexion',
        noAccessTitle: 'Pas d’accès à la discussion',
        noAccessBody: 'Votre compte n’a pas accès à cette application.',
        noAccessAction: 'Aller à mon compte',
    },
    documents: {
        title: 'Documents',
        hint: 'Toutes les discussions de ce projet, et des projets qu’il contient, peuvent les lire.',
        none: 'Aucun document pour l’instant.',
        add: 'Ajouter un document',
        remove: 'Retirer',
        accepts: 'Fichiers PDF, Word, Excel, texte, Markdown et CSV.',
        tokens: '≈ {tokens} jetons',
        afterSaving: 'Les documents pourront être ajoutés une fois le projet enregistré.',
        indexing: 'Lecture en cours…',
        indexed: 'Interrogeable',
        failed: 'Lecture impossible',
    },
    projects: {
        title: 'Projets',
        subtitle: 'Un projet porte une consigne et un modèle par défaut, dont héritent ses sous-projets et ses discussions.',
        none: 'Aucun projet.',
        create: 'Nouveau projet',
        name: 'Nom',
        parent: 'Projet parent',
        noParent: 'Aucun (racine)',
        systemPrompt: 'Consigne',
        systemPromptHint: 'Ajoutée au début de chaque échange. Vous payez ces jetons à chaque tour, alors restez bref.',
        defaultModel: 'Modèle par défaut',
        inherit: 'Hériter',
        effectivePrompt: 'Consigne effective',
        effectivePromptHint: 'La consigne de ce projet précédée de celle de chacun de ses parents.',
        edit: 'Modifier le projet',
        created: 'Projet créé.',
        saved: 'Projet enregistré.',
        deleteConfirm: 'Supprimer ce projet, ses sous-projets et leurs discussions ?',
        tree: 'Arborescence des projets',
    },
    apiKey: {
        title: 'Clé API NVIDIA',
        subtitle: 'La discussion appelle NVIDIA avec votre clé. Elle est chiffrée, ne ressort jamais du serveur, et n’est jamais consignée.',
        label: 'Clé API',
        placeholder: 'nvapi-…',
        hint: 'Créez-en une sur build.nvidia.com, puis collez-la ici.',
        save: 'Enregistrer la clé',
        remove: 'Supprimer la clé',
        saved: 'Clé enregistrée et vérifiée.',
        removed: 'Clé supprimée.',
        configured: 'Clé enregistrée, se terminant par {suffix}.',
        notConfigured: 'Aucune clé enregistrée.',
        validatedAt: 'Vérifiée {when}',
        // The third outcome, and the reason the contract has three: the provider
        // could not be reached, so the key is neither proven good nor proven bad.
        // Saying nothing would let a key that never validated look verified.
        notVerified: 'Pas encore vérifiée — le fournisseur n’a pas pu être joint. La clé est enregistrée et sera utilisée telle quelle.',
        // The provider cannot always be reached, and "we could not tell" must not be
        // shown as "your key is wrong".
        lastError: 'Dernier échec : {message}',
        replaceHint: 'Enregistrer une nouvelle clé remplace celle-ci.',
    },
    photos: {
        title: 'Photos',
        // The grid's accessible name. A list of a thousand tiles with no label is
        // an unnavigable wall to a screen reader.
        gallery: 'Galerie',
        emptyTitle: 'Aucune photo pour l’instant',
        emptyBody: 'Ajoutez vos premières photos : elles sont chiffrées avant de quitter le serveur.',
        add: 'Ajouter des photos',
        addMore: 'Ajouter',
        // `alt` on a tile. The filename is what the person recognises; the date is
        // what tells two files of the same name apart.
        tileAlt: '{filename}, {date}',
        untitled: 'Sans titre',
        loadingMore: 'Chargement des photos…',
        loadMore: 'Charger la suite',
        // The service worker is what turns /media/<id> into a photo, so without it
        // the grid is a wall of broken images rather than a slow one.
        workerUnavailable: 'Votre navigateur ne peut pas déchiffrer les photos ici. Elles ne s’afficheront pas.',
        accepts: 'Images JPEG, PNG, WebP, AVIF, GIF et HEIC, vidéos MP4, MOV et WebM.',
        videoLabel: 'Vidéo',
        // A duration on a video tile, already formatted.
        duration: '{duration}',
        noAccessTitle: 'Pas d’accès aux photos',
        // The full-screen view.
        //
        // The dialog's own accessible name. Its title is the filename, which is
        // what somebody navigating by landmark needs to hear, but "vacances.jpg"
        // on its own does not say what kind of thing has just opened.
        viewer: 'Photo',
        opening: 'Ouverture…',
        // Every link in the chain - a worker that never claimed the page, a key
        // that did not arrive, an expired signature - fails as a picture that does
        // not appear, so the message says no more than is known.
        openFailed: 'Cette photo n’a pas pu être ouverte.',
        previous: 'Photo précédente',
        next: 'Photo suivante',
        // Which photo of how many are loaded. Not of how many exist: the library is
        // paged, and a total nobody has counted would be a number that grows while
        // somebody reads it.
        position: '{index} sur {total}',
        dimensions: '{width} × {height}',
        // For a photo whose file carried no capture time, which is common for
        // screenshots and anything through a messaging app.
        addedOn: 'Ajoutée le {date}',
        // Said after a delete, with the way back beside it. The window is named
        // because "deleted" and "deleted for 30 days" are different promises.
        deleted: '{filename} est dans la corbeille, et y sera gardée {days} jours.',
        undo: 'Annuler',
        restored: '{filename} est de retour dans votre bibliothèque.',
        // The trash.
        trash: 'Corbeille',
        trashTitle: 'Corbeille',
        // Said once at the top, so the rule is stated where somebody is deciding
        // what to do rather than only on the button that put things here.
        trashIntro: 'Les photos restent ici {days} jours, puis elles sont supprimées définitivement.',
        trashEmptyTitle: 'La corbeille est vide',
        trashEmptyBody: 'Les photos que vous supprimez arrivent ici, et vous pouvez les restaurer.',
        backToGallery: 'Retour aux photos',
        restore: 'Restaurer',
        // How long this item has left. Plural in the catalogue rather than computed,
        // because "1 jours" is the sort of thing nobody notices until it ships.
        daysLeft: 'Encore {count} jour | Encore {count} jours',
        // Its window has closed and the next sweep will take it. "0 jour restant"
        // reads as "already gone", which it is not - it is still restorable right
        // up until the sweep runs.
        dueNow: 'Suppression imminente',
        // The purge. Worded as the thing it does, not as "delete" again - the photo
        // has already been deleted once, and the difference is the whole point.
        deleteForGood: 'Supprimer définitivement',
        deleteForGoodTitle: 'Supprimer définitivement {filename} ?',
        // States the irreversibility plainly, because it is genuinely absolute: the
        // key that opens the file lives in the row this destroys.
        deleteForGoodBody: 'Cette action est irréversible. La photo et tout ce qui en a été tiré sont supprimés immédiatement.',
        purged: '{filename} a été supprimée.',
        // Albums.
        albums: 'Albums',
        albumsTitle: 'Albums',
        albumsIntro: 'Rangez vos photos par voyage, par jour, par ce que vous voulez. Un album peut en contenir d’autres.',
        albumsEmptyTitle: 'Aucun album',
        albumsEmptyBody: 'Créez un album, puis ajoutez-y des photos depuis la galerie.',
        newAlbum: 'Nouvel album',
        albumTitle: 'Nom de l’album',
        createAlbum: 'Créer',
        // Creating one inside another, from that album's own row.
        addSubAlbum: 'Ajouter un sous-album',
        renameAlbum: 'Renommer',
        // How many photos are in this album itself - deliberately not counting the
        // ones in the albums beneath it, which would make a parent's number a
        // surprise.
        albumCount: 'aucune photo | 1 photo | {count} photos',
        openAlbum: 'Ouvrir',
        backToAlbums: 'Retour aux albums',
        // Deleting an album. Says what it does *not* do, because that is the part
        // somebody hesitating over the button is actually unsure about.
        deleteAlbumTitle: 'Supprimer l’album {title} ?',
        deleteAlbumBody: 'Les photos ne sont pas supprimées : elles restent dans votre galerie. Les albums contenus dans celui-ci sont supprimés aussi.',
        albumDeleted: 'L’album {title} a été supprimé.',
        albumEmptyTitle: 'Cet album est vide',
        albumEmptyBody: 'Ouvrez une photo dans la galerie et ajoutez-la à cet album.',
        // Filing a photo, from the full-screen view.
        addToAlbum: 'Ajouter à un album',
        addToAlbumTitle: 'Ajouter à un album',
        addedToAlbum: 'Ajoutée à {title}.',
        alreadyInAlbum: 'Déjà dans {title}.',
        removeFromAlbum: 'Retirer de l’album',
        removedFromAlbum: 'Retirée de l’album.',
        noAlbumsYet: 'Créez d’abord un album.',
        // Suggestions d'albums. Le serveur n'envoie que des faits - un lieu, deux
        // dates, un nombre - parce que le travail qui les trouve tourne la nuit,
        // sans personne de connecté et donc sans langue. La phrase est écrite ici.
        suggestionsTitle: 'Suggestions',
        suggestionsIntro: 'Des sorties repérées dans vos photos, d’après la date et le lieu de prise de vue. Rien n’est créé tant que vous n’acceptez pas.',
        // Le titre proposé, repris tel quel comme nom de l'album si personne ne le
        // modifie. Sans lieu, les dates suffisent : « 12 – 15 août 2026 ».
        suggestionTitle: '{place}, {dates}',
        suggestionCreate: 'Créer l’album',
        suggestionDismiss: 'Non merci',
        // Dit ce que « non merci » engage vraiment, parce que c'est définitif.
        suggestionDismissHint: 'Ces photos ne vous seront plus proposées.',
        suggestionPreview: 'Voir les photos',
        suggestionCreated: 'L’album {title} a été créé.',
        suggestionDismissed: 'Suggestion écartée.',
        suggestionMissing: 'Cette suggestion n’existe plus.',
        // La licence des données de lieux (GeoNames, CC BY 4.0) demande d'être
        // citée là où elles sont utilisées.
        suggestionPlaces: 'Lieux : GeoNames',
        // Groupes de partage. Ce qui n'est PAS ici fait partie de la conception :
        // personne ne voit la liste des invitations qu'il a envoyées, parce qu'une
        // invitation qui disparaît révélerait un refus.
        groups: 'Groupes',
        groupsTitle: 'Groupes de partage',
        groupsIntro: 'Partagez un album avec les personnes de votre choix. Tout est privé tant que vous ne partagez rien.',
        groupsEmptyTitle: 'Aucun groupe',
        groupsEmptyBody: 'Créez un groupe, invitez quelqu’un par son adresse e-mail, puis partagez-lui un album.',
        newGroup: 'Nouveau groupe',
        groupName: 'Nom du groupe',
        createGroup: 'Créer',
        openGroup: 'Voir',
        groupOwner: 'propriétaire',
        memberCount: 'aucun membre | 1 membre | {count} membres',
        // Inviter. Le libellé dit « exacte » parce qu'il n'existe aucune recherche :
        // il faut connaître l'adresse.
        inviteLabel: 'Adresse e-mail',
        inviteHint: 'L’adresse exacte de la personne. Aucune recherche n’est possible.',
        sendInvitation: 'Inviter',
        // La même phrase quoi qu'il arrive : le serveur répond de façon identique
        // pour une adresse connue, inconnue, ou déjà membre. Dire autre chose
        // reviendrait à inventer une information que ce client n'a pas.
        invitationSent: 'Si un compte existe à cette adresse, l’invitation lui a été envoyée.',
        invitationsTitle: 'Invitations',
        invitedBy: 'Invitation de {email}',
        acceptInvitation: 'Rejoindre',
        declineInvitation: 'Refuser',
        invitationAccepted: 'Vous avez rejoint {name}.',
        // Ne dit pas que l'expéditeur a été prévenu, parce qu'il ne l'est pas.
        invitationDeclined: 'Invitation refusée.',
        removeMember: 'Retirer',
        memberRemoved: '{email} a été retiré du groupe.',
        leaveGroup: 'Quitter',
        groupLeft: 'Vous avez quitté {name}.',
        deleteGroupTitle: 'Supprimer le groupe {name} ?',
        deleteGroupBody: 'Aucune photo ni aucun album n’est supprimé : les albums partagés avec ce groupe redeviennent privés.',
        groupDeleted: 'Le groupe {name} a été supprimé.',
        // Partager un album.
        shareWith: 'Partagé avec',
        notShared: 'Privé',
        albumShared: '{title} est partagé avec {group}.',
        albumUnshared: '{title} n’est plus partagé.',
        sharedBy: 'partagé par {email}',
        // Le centre de notifications. Chaque phrase est composée ici à partir des
        // faits stockés sur la ligne - un nom de groupe, un titre d’album, une
        // adresse : la personne à l’origine de l’événement et celle qui le lit ne
        // parlent pas forcément la même langue, donc le serveur n’écrit aucune
        // phrase.
        notifications: 'Notifications',
        notificationsUnread: 'Notifications, {count} non lues',
        notificationsEmptyTitle: 'Rien de nouveau',
        notificationsEmptyBody: 'Les invitations et les albums partagés avec vous apparaissent ici.',
        notificationsMore: 'Afficher plus',
        notificationsBadgeMax: '99+',
        // Au présent plutôt qu’au passé composé : « vous a invité » demande un
        // accord avec la personne qui lit, que rien ici ne connaît.
        notifiedInvitation: '{email} vous invite à rejoindre {group}.',
        notifiedMemberJoined: '{email} a rejoint {group}.',
        notifiedAlbumShared: '{email} a partagé {album} avec {group}.',
        // Les quasi-doublons. Rien ici n’est décidé par le balayage : il propose,
        // une personne dispose, et les trois réponses sont les seules sorties.
        duplicates: 'Doublons',
        duplicatesTitle: 'Doublons',
        duplicatesIntro: 'Ces photos se ressemblent beaucoup. Gardez celle que vous préférez, ou gardez-les toutes.',
        duplicatesEmptyTitle: 'Aucun doublon',
        duplicatesEmptyBody: 'Les photos qui se ressemblent sont regroupées ici, chaque nuit.',
        duplicateCount: 'aucune copie | 1 copie | {count} copies',
        duplicateKeeping: 'gardée',
        duplicateChoose: 'Mettre {filename} à la corbeille',
        // Le compte est dans le libellé : c’est la seule action de cet écran qui
        // détruit quelque chose, et savoir combien avant d’appuyer est le minimum.
        duplicateTrash: 'Rien à supprimer | Mettre 1 photo à la corbeille | Mettre {count} photos à la corbeille',
        duplicateKeep: 'Tout garder',
        duplicateRemind: 'Plus tard',
        duplicateKeepHint: 'Tout garder : ces photos ne vous seront plus jamais proposées ensemble.',
        duplicateKept: 'Les {count} photos sont gardées.',
        duplicateReminded: 'Nous vous le redemanderons dans deux semaines.',
        // Volontairement « à la corbeille » et non « supprimées » : elles sont
        // récupérables pendant trente jours, comme toute autre suppression ici.
        duplicateTrashed: 'Aucune photo déplacée. | 1 photo mise à la corbeille. | {count} photos mises à la corbeille.',
        notifiedAccepted: 'Rejoint',
        notifiedDeclined: 'Refusée',
        notifiedExpired: 'N’est plus disponible',
    },
    upload: {
        title: 'Envoi',
        // Progress across the whole selection rather than one file: somebody who
        // dropped forty photos wants to know when they can close the tab.
        progress: '{done} sur {total}',
        hashing: 'Lecture du fichier…',
        checking: 'Vérification…',
        sending: 'Envoi…',
        // Une attente imposée par la limite de débit, et non un échec. Le serveur a
        // demandé une pause ; la file garde sa place et affiche le compte à rebours,
        // car « en attente » sans chiffre ne se distingue pas d’un blocage.
        waiting: 'En attente du serveur…',
        waitingFor: 'Serveur occupé — reprise dans {seconds} s',
        finishing: 'Finalisation…',
        thumbnail: 'Vignette…',
        done: 'Envoyé',
        duplicate: 'Déjà dans la bibliothèque',
        // Distinct from the line above, because the file *is* held and the gallery
        // does not show it. "Already in the library" about a photo somebody cannot
        // find reads as the upload having silently done nothing.
        trashed: 'Déjà ici, dans la corbeille',
        failed: 'Échec',
        retry: 'Réessayer',
        cancel: 'Annuler',
        dismiss: 'Fermer',
        // Said once, when the whole batch has finished.
        allDone: '{count} photo(s) ajoutée(s).',
        someFailed: '{count} envoi(s) ont échoué.',
        tooLarge: 'Ce fichier dépasse la taille maximale de {limit}.',
        unsupported: 'Ce format n’est pas accepté.',
    },
    status: {
        pending_verification: 'En attente de vérification',
        active: 'Actif',
        locked: 'Bloqué',
        deleting: 'Suppression en cours',
    },
    // The Android app. Only the WebView says any of this: what the phone says
    // on its own - an upload notification, the outdated-WebView screen - is in
    // the app's Android string resources, because no WebView is running then.
    mobile: {
        // Offered instead of the sign-in form when the phone still holds a session
        // and simply could not reach the server with it. Asking for a password and
        // a second factor again would cost somebody a sign-in for a tunnel.
        unreachableTitle: 'Serveur injoignable',
        unreachableBody: 'Vous êtes toujours connecté sur ce téléphone. Vérifiez votre connexion, puis réessayez.',
        // Beside the links that leave the app: creating an account and resetting a
        // password happen on the website, in the browser.
        inBrowser: 'S’ouvre dans votre navigateur.',
        // The automatic backup, and the phones that make one. The same screen in
        // the app and on the website: the app adds this phone's settings above
        // the list, the website only lists.
        backupTitle: 'Sauvegarde automatique',
        backupIntro: 'Les photos des dossiers choisis partent d’elles-mêmes : chaque heure, quand la batterie n’est pas faible, sur le réseau que vous autorisez.',
        backupIntroWeb: 'La sauvegarde automatique se règle depuis l’application Android. Voici les téléphones qui sauvegardent vers ce compte.',
        thisPhone: 'Ce téléphone',
        // No access at all, or only the photos somebody picked. The second is a
        // perfectly good answer for most apps and a useless one for a backup,
        // which would never include tomorrow's photos - so it is explained.
        accessNone: 'Autorisez l’accès à vos photos pour choisir les dossiers à sauvegarder.',
        accessPartial: 'Seules certaines photos sont autorisées. Une sauvegarde automatique a besoin de toutes : sinon, les nouvelles photos n’en feraient jamais partie.',
        allowAccess: 'Autoriser l’accès',
        openSettings: 'Ouvrir les réglages',
        noLocation: 'Sans accès à leur position, les photos partent sans leur lieu, et aucun album ne pourra être proposé pour elles.',
        folders: 'Dossiers à sauvegarder',
        folderItems: 'vide | 1 élément | {count} éléments',
        noFolders: 'Aucun dossier de ce téléphone ne contient de photos.',
        // Said once the limit the account keeps for one phone is reached: the
        // remaining boxes are greyed out, and this says how to choose another.
        folderLimit: 'Un téléphone peut sauvegarder au plus {max} dossiers. Décochez-en un pour en choisir un autre.',
        // Files the phone gave up reading after five runs. A network failure is
        // never counted, so this is always about the file itself.
        unreadable: 'Toutes les photos ont pu être lues. | Cette photo n’a pas pu être lue après cinq essais et n’est pas sauvegardée : | Ces {count} photos n’ont pas pu être lues après cinq essais et ne sont pas sauvegardées :',
        unreadableMore: 'rien de plus | et 1 autre | et {count} autres',
        // The accessible name of the per-folder network choice.
        networkFor: 'Réseau pour {folder}',
        networkUnmetered: 'Wi-Fi uniquement',
        networkAny: 'Wi-Fi et données mobiles',
        backUpNow: 'Sauvegarder maintenant',
        lastBackup: 'Dernière sauvegarde {when}',
        neverBackedUp: 'Pas encore de sauvegarde.',
        uploadedCount: 'aucune photo envoyée | 1 photo envoyée | {count} photos envoyées',
        pendingCount: 'rien en attente | 1 en attente | {count} en attente',
        // How the last background run ended. Each state is a code the phone
        // reports; the sentence is ours.
        runState: {
            running: 'Sauvegarde en cours…',
            'signed-out': 'Sauvegarde en pause : reconnectez-vous.',
            permission: 'Sauvegarde en pause : l’accès aux photos a été retiré.',
            quota: 'Sauvegarde en pause : l’espace de votre compte est plein.',
            removed: 'Ce téléphone a été retiré du compte. Choisissez des dossiers pour reprendre.',
            failed: 'La dernière sauvegarde n’a pas abouti ; elle sera retentée.',
            'update-required': 'La sauvegarde était en pause en attendant la mise à jour de l’application. Elle reprend à sa prochaine exécution.',
        },
        phones: 'Téléphones de ce compte',
        noPhones: 'Aucun téléphone ne sauvegarde vers ce compte.',
        phoneFolders: 'aucun dossier | 1 dossier | {count} dossiers',
        removePhone: 'Retirer',
        removePhoneTitle: 'Retirer {name} ?',
        removePhoneBody: 'Il cessera de sauvegarder à sa prochaine tentative. Les photos déjà envoyées restent dans votre bibliothèque.',
    },
    apps: {
        identity: 'Compte',
        chat: 'Discussion',
        photos: 'Photos',
    },
    // The page a visitor without a session lands on, honore-bagni.fr included.
    welcome: {
        title: 'Bienvenue',
        subtitle: 'Un espace privé pour la famille : un seul compte pour discuter avec une IA et garder vos photos.',
        appsHeading: 'Les applications',
        chat: 'Discutez avec des modèles d’IA, y compris sur vos propres documents, et reprenez une conversation là où vous l’aviez laissée.',
        photos: 'Vos photos et vidéos, chiffrées, rangées en albums et partagées avec les proches que vous choisissez.',
        identity: 'Un seul compte pour tout, protégé par une double authentification.',
    },
    metrics: {
        storage_bytes: 'Stockage',
        media_items: 'Photos et vidéos',
        albums: 'Albums',
        ai_messages_per_month: 'Messages IA ce mois-ci',
        ai_projects: 'Projets IA',
        groups: 'Groupes',
    },
    errors: {
        'validation.failed': 'Certaines informations sont invalides. Vérifiez le formulaire.',
        'auth.unauthenticated': 'Vous devez être connecté pour continuer.',
        'auth.invalid_credentials': 'Adresse e-mail ou mot de passe incorrect.',
        'auth.email_not_verified': 'Vérifiez votre adresse e-mail avant de continuer.',
        'auth.totp_required': 'Saisissez votre code d’authentification.',
        'auth.totp_invalid': 'Ce code est incorrect ou a expiré.',
        'auth.totp_enrolment_required': 'Configurez la double authentification pour activer votre compte.',
        'auth.account_locked': 'Ce compte est temporairement bloqué. Réessayez plus tard.',
        'auth.account_deletion_scheduled': 'Ce compte est en cours de suppression. Pour le garder, suivez le lien de l’e-mail que nous vous avons envoyé.',
        'auth.session_expired': 'Votre session a expiré. Reconnectez-vous.',
        'auth.forbidden': 'Vous n’avez pas accès à cette ressource.',
        'auth.reauthentication_required': 'Confirmez votre identité pour effectuer cette action.',
        'resource.not_found': 'Cet élément est introuvable.',
        'resource.conflict': 'Cette action entre en conflit avec l’état actuel.',
        'resource.gone': 'Cet élément n’est plus disponible.',
        'upload.duplicate': 'Ce fichier a déjà été importé.',
        'upload.too_large': 'Ce fichier est trop volumineux.',
        'upload.unsupported_type': 'Ce type de fichier n’est pas pris en charge.',
        'upload.no_text': 'Aucun texte n’a pu être lu dans ce fichier. Un PDF numérisé doit d’abord passer par une reconnaissance de caractères.',
        'upload.incomplete': 'Cet envoi n’est pas terminé : certaines parties du fichier manquent encore.',
        'quota.exceeded': 'Vous avez atteint la limite de votre formule.',
        'rate_limit.exceeded': 'Trop de tentatives. Patientez un instant.',
        'credentials.missing': 'Ajoutez votre clé API dans vos réglages pour commencer à discuter.',
        'stream.capacity_exceeded': 'Trop de réponses sont en cours d’écriture. Réessayez dans un instant.',
        'upstream.unavailable': 'Le service distant est indisponible. Réessayez plus tard.',
        'upstream.invalid_credentials': 'La clé API a été refusée par le fournisseur. Vérifiez-la dans vos réglages.',
        'upstream.content_filtered': 'Le modèle a refusé de répondre à cette demande.',
        // Nomme la cause : « une erreur est survenue » enverrait chercher au mauvais
        // endroit, puisque le modèle a fonctionné tout du long sans jamais répondre.
        'upstream.no_answer': 'Le modèle a passé toute sa réponse à réfléchir sans jamais répondre. Essayez une question plus courte, ou un autre modèle.',
        // Renvoie vers le compte du fournisseur plutôt que vers un autre modèle :
        // ni le modèle ni la clé ne sont en cause, c'est le compte qui n'a pas accès
        // à celui-ci.
        'upstream.model_unavailable': 'Votre clé API n’a pas accès à ce modèle. Choisissez-en un autre, ou activez-le sur votre compte NVIDIA.',
        // Pas une erreur interne, et la formulation est l’essentiel : les photos
        // sont intactes, un tiers a un souci passager, et réessayer dans un instant
        // est la bonne réaction. La galerie met un bouton à côté plutôt que de
        // laisser deviner quand.
        'storage.unavailable': 'Les photos sont momentanément indisponibles. Rien n’est perdu — réessayez dans un instant.',
        'app.update_required': 'Cette version de l’application n’est plus prise en charge. Mettez-la à jour depuis le Play Store.',
        'server.internal': 'Une erreur inattendue est survenue.',
        network: 'Connexion impossible. Vérifiez votre accès à Internet.',
        unknown: 'Une erreur inattendue est survenue.',
    },
};
// Deliberately NOT `as const`. With it, `typeof fr` would be a set of literal
// string types, and `en: typeof fr` would demand the English text be character
// for character identical to the French. Widening the values to `string` keeps
// the property names checked - which is the constraint that matters - while
// letting the translations differ.
//# sourceMappingURL=fr.js.map